// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { formatHumanName } from '@medplum/core';
import type {
  Attachment,
  Practitioner,
  PractitionerQualification,
  PractitionerRole,
  ProjectMembership,
} from '@medplum/fhirtypes';
import { POLARIS_PROVIDER_ROLE_SYSTEM, POLARIS_PROVIDER_ROLES } from '../../constants/appointmentTypes';
import {
  CREDENTIAL_SYSTEM,
  DEA_SYSTEM,
  getIdentifier,
  getQualificationsBySystem,
  getQualificationText,
  getTelecom,
  isPractitionerOnboardingPending,
  LICENSE_SYSTEM,
  NPI_SYSTEM,
  PTAN_SYSTEM,
} from '../../utils/practitioner';

export const EMPTY_VALUE = '—';

export interface StaffRow {
  practitioner: Practitioner;
  practitionerRole: PractitionerRole | undefined;
  name: string;
  status: 'Active' | 'Inactive';
  accountType: string;
  email: string;
  npi: string;
  licenseStates: string[];
  /**
   * True when the Practitioner carries the `onboarding-pending` tag.
   * Used to gate inline Resend / Cancel invite actions on the Staffing table.
   * Phase 4 (cancel-invite) also reads this field — keep the name general.
   */
  isOnboardingPending: boolean;
  /**
   * True when the provider's ProjectMembership is active (or when no
   * membership is present — defaults to true so new invites are not
   * shown as disabled). False when `membership.active === false`.
   *
   * Used to gate the Disable / Enable actions on the Staffing table.
   */
  isActive: boolean;
}

/**
 * Returns the human-readable name for a Practitioner. Falls back to the FHIR id
 * (or EMPTY_VALUE) when no `name` is present so the table never renders blank.
 */
export function getPractitionerName(p: Practitioner): string {
  const first = p.name?.[0];
  if (first) {
    const formatted = formatHumanName(first);
    if (formatted.trim()) {
      return formatted;
    }
  }
  return p.id ?? EMPTY_VALUE;
}

/**
 * Pulls the Polaris role coding off a PractitionerRole, ignoring any non-Polaris
 * codes. Mirrors the resolution used by `useRole`.
 */
export function getRoleCode(role: PractitionerRole | undefined): string | undefined {
  return role?.code?.[0]?.coding?.find((c) => c.system === POLARIS_PROVIDER_ROLE_SYSTEM)?.code;
}

export function getRoleDisplay(role: PractitionerRole | undefined): string {
  const code = getRoleCode(role);
  if (code && code in POLARIS_PROVIDER_ROLES) {
    return POLARIS_PROVIDER_ROLES[code as keyof typeof POLARIS_PROVIDER_ROLES].display;
  }
  const coding = role?.code?.[0]?.coding?.find((c) => c.system === POLARIS_PROVIDER_ROLE_SYSTEM);
  return coding?.display ?? EMPTY_VALUE;
}

/**
 * Account type shown in the staffing UI. A project-admin ProjectMembership
 * (`membership.admin === true`) is surfaced as "Administrator" regardless of the
 * provider's clinical PractitionerRole coding — mirroring how the
 * `invite-provider` / `change-provider-role` bots treat admin as a top-level
 * account type. Falls back to the PractitionerRole display otherwise.
 */
export function getAccountType(role: PractitionerRole | undefined, membership?: ProjectMembership | undefined): string {
  if (membership?.admin === true) {
    return 'Administrator';
  }
  return getRoleDisplay(role);
}

export function getStatus(p: Practitioner, role: PractitionerRole | undefined): 'Active' | 'Inactive' {
  if (p.active === false) {
    return 'Inactive';
  }
  if (role && role.active === false) {
    return 'Inactive';
  }
  return 'Active';
}

export function getEmail(p: Practitioner): string {
  return getTelecom(p, 'email') || EMPTY_VALUE;
}

export function getNpi(p: Practitioner): string {
  return getIdentifier(p, NPI_SYSTEM) || EMPTY_VALUE;
}

export function getLicenseStates(p: Practitioner): string[] {
  return (p.qualification ?? [])
    .map((q) => {
      const coding = q.code?.coding?.find((c) => c.system === LICENSE_SYSTEM);
      return coding?.code ?? '';
    })
    .filter((s): s is string => Boolean(s));
}

export function formatLicenseStates(states: string[]): string {
  return states.length > 0 ? states.join(', ') : EMPTY_VALUE;
}

export function buildStaffRow(
  p: Practitioner,
  role: PractitionerRole | undefined,
  membership?: ProjectMembership | undefined
): StaffRow {
  return {
    practitioner: p,
    practitionerRole: role,
    name: getPractitionerName(p),
    status: getStatus(p, role),
    accountType: getAccountType(role, membership),
    email: getEmail(p),
    npi: getNpi(p),
    licenseStates: getLicenseStates(p),
    isOnboardingPending: isPractitionerOnboardingPending(p),
    // Default to true when membership is absent — a missing membership should not
    // render the provider as disabled (e.g. during the window between Practitioner
    // creation and the ProjectMembership being established).
    isActive: membership?.active !== false,
  };
}

/**
 * Lower-case substring filter across the table's searchable columns.
 * Matches name, email, NPI, or any license state code.
 */
export function matchesSearch(row: StaffRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  if (row.name.toLowerCase().includes(q)) {
    return true;
  }
  if (row.email.toLowerCase().includes(q)) {
    return true;
  }
  if (row.npi.toLowerCase().includes(q)) {
    return true;
  }
  if (row.licenseStates.some((s) => s.toLowerCase().includes(q))) {
    return true;
  }
  return false;
}

export function countRoleAssignments(roles: PractitionerRole[], roleCode: string): number {
  let count = 0;
  for (const r of roles) {
    if (getRoleCode(r) === roleCode && r.active !== false) {
      count += 1;
    }
  }
  return count;
}

/**
 * Picks the PractitionerRole that carries a Polaris role coding, preferring it
 * over arbitrary first-matches. Falls back to the first role linked to the
 * practitioner so the caller can still report status/active state. Returns
 * `undefined` when none of the candidates link back to this practitioner.
 */
export function pickPrimaryRole(roles: PractitionerRole[]): PractitionerRole | undefined {
  if (roles.length === 0) {
    return undefined;
  }
  const polaris = roles.find((r) => getRoleCode(r) !== undefined);
  return polaris ?? roles[0];
}

// ─── Detail-page helpers ─────────────────────────────────────────────────────

export function getTitle(p: Practitioner): string {
  return p.name?.[0]?.prefix?.[0] ?? '';
}

export function getFirstName(p: Practitioner): string {
  return p.name?.[0]?.given?.[0] ?? '';
}

export function getLastName(p: Practitioner): string {
  return p.name?.[0]?.family ?? '';
}

export function getPhone(p: Practitioner): string {
  return getTelecom(p, 'phone') || EMPTY_VALUE;
}

export function getDea(p: Practitioner): string {
  return getIdentifier(p, DEA_SYSTEM) || EMPTY_VALUE;
}

export function getPtan(p: Practitioner): string {
  return getIdentifier(p, PTAN_SYSTEM) || EMPTY_VALUE;
}

export function getDegree(p: Practitioner): string {
  return getQualificationText(p.qualification ?? [], 'degree') || EMPTY_VALUE;
}

export function getCertification(p: Practitioner): string {
  return getQualificationText(p.qualification ?? [], 'certification') || EMPTY_VALUE;
}

export function getTaxonomy(p: Practitioner): string {
  return getQualificationText(p.qualification ?? [], 'taxonomy') || EMPTY_VALUE;
}

export function getBirthDate(p: Practitioner): string {
  return p.birthDate || EMPTY_VALUE;
}

export function getPhoto(p: Practitioner): Attachment | undefined {
  return p.photo?.[0];
}

export interface CredentialSummary {
  payor: string;
  status: string;
  planId: string;
}

export function getCredentialSummaries(p: Practitioner): CredentialSummary[] {
  const quals: PractitionerQualification[] = getQualificationsBySystem(p.qualification ?? [], CREDENTIAL_SYSTEM);
  return quals.map((q) => ({
    payor: q.issuer?.display ?? EMPTY_VALUE,
    status: q.extension?.find((e) => e.url === 'status')?.valueString ?? EMPTY_VALUE,
    planId: q.identifier?.[0]?.value ?? EMPTY_VALUE,
  }));
}
